import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Users, Clock, Package, TrendingUp, AlertCircle, Plus } from 'lucide-react';

export function Dashboard() {
  const stats = [
    {
      title: 'Total Clients',
      value: '124',
      change: '+12%',
      changeType: 'positive' as const,
      icon: Users,
    },
    {
      title: 'Active Subscriptions',
      value: '89',
      change: '+5%',
      changeType: 'positive' as const,
      icon: Clock,
    },
    {
      title: 'Monthly Revenue',
      value: '₱684,750',
      change: '+18%',
      changeType: 'positive' as const,
      icon: TrendingUp,
    },
    {
      title: 'Hours Consumed',
      value: '2,340',
      change: '+8%',
      changeType: 'positive' as const,
      icon: Package,
    },
  ];

  const recentActivity = [
    { client: 'John Doe', action: 'Started Premium subscription', time: '2 hours ago' },
    { client: 'Sarah Wilson', action: 'Purchased 30-hour package', time: '4 hours ago' },
    { client: 'Mike Johnson', action: 'Used 3 hours', time: '6 hours ago' },
    { client: 'Emma Brown', action: 'Renewed Monthly subscription', time: '1 day ago' },
  ];

  const expiringSoon = [
    { client: 'Alex Turner', subscription: 'Premium', expires: '3 days', hours: 12 },
    { client: 'Lisa Chen', subscription: '30-hour package', expires: '5 days', hours: 5 },
    { client: 'David Park', subscription: 'Regular', expires: '1 week', hours: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">{stat.title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  <span className={stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}>
                    {stat.change}
                  </span>
                  {' '}from last month
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest client actions and updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium leading-none">{activity.client}</p>
                    <p className="text-sm text-muted-foreground">{activity.action}</p>
                  </div>
                  <div className="text-sm text-muted-foreground">{activity.time}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Expiring Soon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              Expiring Soon
            </CardTitle>
            <CardDescription>Subscriptions requiring attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {expiringSoon.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{item.client}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{item.subscription}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {item.hours} hours left
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-orange-600">
                    {item.expires}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add New Client
            </Button>
            <Button variant="outline">
              <Package className="mr-2 h-4 w-4" />
              Create Package
            </Button>
            <Button variant="outline">
              <Clock className="mr-2 h-4 w-4" />
              Log Hours
            </Button>
            <Button variant="outline">
              <TrendingUp className="mr-2 h-4 w-4" />
              View Reports
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}